import React__default, { useState, useRef, useCallback } from 'react';
import { u as useInlinePopover } from '../Popover/useInlinePopover-D_iJ07xC.js';
import { P as PopoverTrigger } from '../Popover/Popover.styles-C6137wzj.js';
import { g as getPlacement } from './utils-BDd8EZN8.js';
import { D as DEFAULT_TRANSITION_DURATION } from './constants-DsghXWMj.js';
import { K as KEYBOARD_KEYNAMES } from '../LocalModules/storm-ui-utils/dist/esm/keyboardKeynames-AmBipDtf-AmBipDtf.js';
import { t as trapTabFocus } from '../LocalModules/storm-ui-utils/dist/esm/focusableElements/trapTabFocus-vpg0lK2r-Bk1xQ7S4.js';
import { g as getFocusableElements } from '../LocalModules/storm-ui-utils/dist/esm/focusableElements/getFocusableElements-BjFtn5aB-BjFtn5aB.js';

function InteractiveContainer({
  align,
  children,
  forceOpen,
  inert,
  onClick,
  onCloseButtonClick,
  position,
  spacing,
  transitionDuration,
  trigger,
  fullWidthTrigger,
  withCloseButton
}) {
  const [isOpen, setIsOpen] = useState(forceOpen);
  const [showFocus, setShowFocus] = useState(false);
  const timeout = useRef(undefined);
  const placement = getPlacement(align, position);
  const {
    attributes,
    styles,
    triggerElement,
    popoverElement,
    setPopoverElement,
    setTriggerElement,
    update
  } = useInlinePopover({
    offsetDistance: spacing,
    placement,
    enableFlip: true,
    rootBoundary: 'viewport'
  });

  /**
   * Handles mouse clicks on the <Tooltip /> close button.
   */
  const handleCloseClicked = useCallback(event => {
    event.stopPropagation();
    setIsOpen(false);

    // call the callback function passed via props.
    onCloseButtonClick === null || onCloseButtonClick === void 0 || onCloseButtonClick(event);
  }, [onCloseButtonClick]);

  /**
   * Handles the mouse hovering over the <Tooltip /> trigger.
   */
  const handleMouseEnterTrigger = useCallback(() => {
    var _transitionDuration$e;
    if (inert) {
      return;
    }
    const duration = typeof transitionDuration === 'number' ? transitionDuration : (_transitionDuration$e = transitionDuration.enter) !== null && _transitionDuration$e !== void 0 ? _transitionDuration$e : DEFAULT_TRANSITION_DURATION.enter;
    clearTimeout(timeout.current);
    setShowFocus(false);
    timeout.current = window.setTimeout(() => {
      setIsOpen(true);
    }, duration);
  }, [inert, transitionDuration]);

  /**
   * Handles the mouse leaving the <Tooltip /> trigger.
   */
  const handleMouseLeaveTrigger = useCallback(() => {
    var _transitionDuration$e2;
    const duration = typeof transitionDuration === 'number' ? transitionDuration : (_transitionDuration$e2 = transitionDuration.exit) !== null && _transitionDuration$e2 !== void 0 ? _transitionDuration$e2 : DEFAULT_TRANSITION_DURATION.exit;
    clearTimeout(timeout.current);
    timeout.current = window.setTimeout(() => {
      setIsOpen(false);
    }, duration);
  }, [transitionDuration]);

  /**
   * Handles keyboard input on the <Tooltip /> trigger.
   *
   * Should open the <Tooltip /> if Enter or Space are clicked and focus should be visible.
   */
  const handleKeyDownTrigger = useCallback(event => {
    if (inert) {
      return;
    }
    const {
      key
    } = event;
    if (key === KEYBOARD_KEYNAMES.ENTER || key === KEYBOARD_KEYNAMES.SPACE) {
      clearTimeout(timeout.current);
      event.preventDefault();
      setShowFocus(true);
      setIsOpen(true);
    }
  }, [inert]);

  /**
   * Handles the mouse hovering over the <Tooltip /> surface. It should
   * stop the <Tooltip /> from closing.
   *
   * Because it is mouse input, focus should not be visible.
   */
  const handleMouseEnterTooltip = useCallback(() => {
    // clear timeout created when the mouse left the <Tooltip /> trigger
    clearTimeout(timeout.current);
  }, []);

  /**
   * Handles the mouse leaving the <Tooltip /> surface. It should
   * close the <Tooltip /> unless it is being forced open.
   *
   * Because it is mouse input, focus should not be visible.
   */
  const handleMouseLeaveTooltip = useCallback(() => {
    if (!forceOpen) {
      setIsOpen(false);
    }
  }, [forceOpen]);

  /**
   * Handles keyboard KeyDown input when the <Tooltip /> surface is in focus.
   */
  const handleKeyDownTooltip = useCallback(event => {
    const {
      key
    } = event;
    if (popoverElement) {
      // Trap the focus of the browser inside the popover element. Keep the user in this element
      // and allow them to tab through content until the user closes the <Tooltip />.
      trapTabFocus(event, popoverElement, false);
    }
    if (key === KEYBOARD_KEYNAMES.SPACE) {
      event.preventDefault();
    }
  }, [popoverElement]);

  /**
   * Handles keyboard input when the <Tooltip /> surface is in focus.
   */
  const handleKeyUpTooltip = useCallback(event => {
    const {
      key
    } = event;

    // Close the <Tooltip /> if the Escape key is pressed
    if (key === KEYBOARD_KEYNAMES.ESCAPE) {
      event.stopPropagation();
      setIsOpen(false);
    }
  }, []);

  /**
   * Catches the click event from the <Tooltip /> trigger and only
   * call the 'onClick' callback if it was passed.
   */
  const preventTriggerClickThrough = useCallback(event => {
    event.stopPropagation();
    if (onClick) {
      onClick(event);
    }
  }, [onClick]);

  /**
   * Function called once the <Transition /> component is in the entered state.
   *
   * If we are showing focus because we have had some keyboard input, we should set
   * focus to the Popover element.
   */
  const handleTooltipEntered = useCallback(() => {
    if (popoverElement) {
      if (showFocus) {
        const focusableEls = getFocusableElements(popoverElement);
        if (focusableEls.length > 0) {
          // e.g. In this case, will likely be the Close button
          focusableEls[0].focus();
        }
      }
    }
  }, [showFocus, popoverElement]);
  const handleTooltipEntering = useCallback(isAppearing => {
    if (!isAppearing && update !== null) {
      update();
    }
  }, [update]);

  /**
   * Function called once the <Transition /> component is in the exited state.
   *
   * If we are showing focus because we have had some keyboard input, we should
   * return focus back to the <Trigger /> element.
   */
  const handleTooltipExited = useCallback(() => {
    if (showFocus) {
      const focusableEls = getFocusableElements(triggerElement);
      if (focusableEls.length > 0) {
        // e.g. tooltip trigger element has a span wrapper
        focusableEls[0].focus();
      }
    }
  }, [triggerElement, showFocus]);
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(PopoverTrigger, {
    onClick: preventTriggerClickThrough,
    onKeyDown: handleKeyDownTrigger,
    ref: setTriggerElement,
    $fullWidth: fullWidthTrigger
  }, /*#__PURE__*/React__default.cloneElement(trigger, {
    onMouseEnter: handleMouseEnterTrigger,
    onMouseLeave: handleMouseLeaveTrigger
  })), children({
    attributes,
    handleCloseClicked,
    handleKeyDown: handleKeyDownTooltip,
    handleKeyUp: handleKeyUpTooltip,
    handleMouseEnter: handleMouseEnterTooltip,
    handleMouseLeave: handleMouseLeaveTooltip,
    handlePopoverEntered: handleTooltipEntered,
    handlePopoverEntering: handleTooltipEntering,
    handlePopoverExited: handleTooltipExited,
    isOpen,
    setPopoverElement,
    styles,
    withCloseButton
  }));
}

export { InteractiveContainer as I };
//# sourceMappingURL=InteractiveContainer-5hWy1a7C.js.map
